import type { DividerHardware, DividerPolicy, LoaderMode } from "@CommonTypes/backend/PspRules";
import {
  getBaseDividerEvidence,
  getBaseLoaderEvidence,
  keptFixtureDividerShelves,
  loaderShelfKey,
  resolveDividerPolicy,
  summarizeBaseLoaders,
  type BaseLoaderEvidence,
  type FixtureDividerRow,
  type LoaderLibraryShelf,
} from "@CommonUtils/pog/dividerPolicy";
import {
  getPlanogramItemFacingWidth,
  type PlanogramItemDimensionFields,
} from "@CommonTypes/merchflow/pog/item";
import { getActiveShelfDividers } from "@CommonUtils/pog/shelfDividers";
import type { TellOBasePog, TellOLevelNames, TellOProduct } from "./products";
import type { TellOUnit } from "./proposal";

/** Everything the rules window already holds; the backend loads the same for the flow. */
export interface TellOContext {
  /** The rule as it stands in the window. */
  policy: DividerPolicy;
  products: readonly TellOProduct[];
  levelNames?: TellOLevelNames;
  basePog: TellOBasePog | null;
  /** The base store's Fixtures page rows (GET base-pog `loaders.libraryShelves`). */
  libraryShelves?: readonly LoaderLibraryShelf[];
  /** The base store's fixture rows (GET base-pog `fixtureDividerRows`); null: not known. */
  fixtureRows?: readonly FixtureDividerRow[] | null;
  unit: TellOUnit;
  /** The base planogram's store, the target of a per-shelf ask. */
  baseStoreCode: string | null;
}

/** A per-shelf ask shown in the preview, on the base store only. */
export interface TellOShelfOverridePreview {
  bayNo: number;
  /** From the top, as the requests number shelves. */
  shelfNo: number;
  dividersSetByHand?: "always" | "never" | null;
  /** x100; 0 = none, null = follow the rule. */
  loaderDepth?: number | null;
}

export interface TellOTicks {
  dividers: Set<string>;
  loaders: Set<string>;
}

/** What each tick column holds under a rule, whatever its source. */
export const tellOTicks = (
  context: Pick<TellOContext, "products" | "basePog" | "libraryShelves">,
  policy: DividerPolicy,
): TellOTicks => {
  const resolution = resolveDividerPolicy({
    policy,
    products: context.products,
    basePog: context.basePog,
    baseLibraryShelves: context.libraryShelves,
  });
  const needed = (needs: Map<string, { needed: boolean }>) =>
    new Set([...needs].filter(([, need]) => need.needed).map(([code]) => code));
  return { dividers: needed(resolution.needs), loaders: needed(resolution.loaders.needs) };
};

/** Product codes in the window's list order. */
export const inListOrder = (products: readonly TellOProduct[], codes: ReadonlySet<string>) =>
  products.map((product) => product.productCode).filter((code) => codes.has(code));

export interface TellOFacts {
  /** {m} */
  products: number;
  /** {n} for dividers. */
  dividerProducts: number;
  /** {t}: base shelves holding products. */
  shelves: number;
  /** {s} for dividers. */
  dividerShelves: number;
  hardware: DividerHardware | null;
  /** {n} for spring loaders. */
  loaderProducts: number;
  /** {u}: shelves due a loader, with or without a depth, as the O line counts them. */
  loaderShelves: number;
  /** {t} for spring loaders. */
  loaderShelvesTotal: number;
  /** {d}, ascending, x100. */
  loaderDepths: number[];
  loaderMode: LoaderMode;
  keep: boolean;
  /** For "Wider dividers take facings": the most common shelf width and facing width, x100. */
  typicalShelfWidth: number | null;
  typicalFacingWidth: number | null;
}

type BaseShelf = NonNullable<
  NonNullable<NonNullable<TellOBasePog["planogram"]>["bays"]>[number]["shelves"]
>[number];

const hasValidShelfDividers = (shelf: BaseShelf) => {
  const active = getActiveShelfDividers(shelf.dividers);
  if (!active) return false;
  const flagsOk = [active.atStart, active.atEnd].every((flag) => Number(flag ?? 0) <= 1);
  return flagsOk && !(Number(shelf.width) > 0 && Number(active.width) >= Number(shelf.width));
};

/** Shelves numbered per bay top first by notch, stored order breaking ties, as the requests do. */
const topFirstNumbers = (shelves: readonly BaseShelf[]) => {
  const numbers = new Map<number, number>();
  shelves
    .map((shelf, index) => ({ notch: Number(shelf.notchNo) || 0, index }))
    .sort((a, b) => b.notch - a.notch || a.index - b.index)
    .forEach(({ index }, position) => numbers.set(index, position + 1));
  return numbers;
};

/** The rules window's `baseShelfDividerResults` count, with a per-shelf preview on top. */
export const tellODividerShelves = (
  context: Pick<TellOContext, "basePog" | "fixtureRows">,
  withDividers: ReadonlySet<string>,
  keepFixtureDividers: boolean,
  override?: TellOShelfOverridePreview | null,
) => {
  const fixtureRows = context.fixtureRows;
  const keptRows = fixtureRows
    ? keptFixtureDividerShelves({ policy: { keepFixtureDividers }, rows: fixtureRows })
    : null;
  const savedSetByHand = new Map(
    (fixtureRows ?? []).map((row) => [
      loaderShelfKey(row.bayNo, row.shelfNo),
      row.dividersSetByHand,
    ]),
  );
  let shelves = 0;
  let dividerShelves = 0;
  (context.basePog?.planogram?.bays ?? []).forEach((bay, bayIndex) => {
    const bayShelves = bay.shelves ?? [];
    const fromTop = topFirstNumbers(bayShelves);
    bayShelves.forEach((shelf, shelfIndex) => {
      if (shelf.type && shelf.type !== "REGULAR") return;
      const items = shelf.items ?? [];
      if (items.length === 0) return;
      let needing = 0;
      let facings = 0;
      for (const item of items) {
        const itemFacings = Math.max(1, Number(item.facings) || 0);
        facings += itemFacings;
        if (withDividers.has(item.productCode)) needing += itemFacings;
      }
      const byProducts = needing * 2 > facings;
      const bayNo = bayIndex + 1;
      const shelfNo = fromTop.get(shelfIndex) ?? shelfIndex + 1;
      const keptFromFixture =
        keepFixtureDividers &&
        !byProducts &&
        fixtureRows !== null &&
        (keptRows ? keptRows.has(loaderShelfKey(bayNo, shelfNo)) : hasValidShelfDividers(shelf));
      const pending =
        override && override.bayNo === bayNo && override.shelfNo === shelfNo
          ? override.dividersSetByHand
          : undefined;
      const setByHand =
        pending !== undefined ? pending : savedSetByHand.get(loaderShelfKey(bayNo, shelfNo));
      const hasDividers =
        setByHand === "never"
          ? false
          : setByHand === "always"
            ? true
            : byProducts || keptFromFixture;
      shelves += 1;
      if (hasDividers) dividerShelves += 1;
    });
  });
  return { shelves, dividerShelves };
};

const typicalFacingWidth = (basePog: TellOBasePog | null) => {
  const widths: number[] = [];
  for (const bay of basePog?.planogram?.bays ?? []) {
    for (const shelf of bay.shelves ?? []) {
      for (const item of shelf.items ?? []) {
        // As the layout steps facings: merchandising style, squeeze, orientation.
        // A stored item carries every field; one without a unit width gives NaN, skipped.
        const width = getPlanogramItemFacingWidth(
          item as typeof item & PlanogramItemDimensionFields,
        );
        if (Number.isFinite(width) && width > 0) widths.push(width);
      }
    }
  }
  if (widths.length === 0) return null;
  widths.sort((a, b) => a - b);
  return widths[Math.floor(widths.length / 2)];
};

const withLoaderOverride = (
  libraryShelves: readonly LoaderLibraryShelf[],
  override: TellOShelfOverridePreview | null | undefined,
): readonly LoaderLibraryShelf[] => {
  if (!override || override.loaderDepth === undefined) return libraryShelves;
  const others = libraryShelves.filter(
    (row) => !(row.bayNo === override.bayNo && row.shelfNo === override.shelfNo),
  );
  return [
    ...others,
    { bayNo: override.bayNo, shelfNo: override.shelfNo, loaderDepth: override.loaderDepth },
  ];
};

export const tellOLoaderEvidence = (
  context: Pick<TellOContext, "basePog" | "libraryShelves">,
): BaseLoaderEvidence => getBaseLoaderEvidence(context.basePog, context.libraryShelves ?? []);

/** Every number O's sentences say, from the real base planogram and the rule. */
export const tellOFacts = (
  context: TellOContext,
  policy: DividerPolicy,
  override?: TellOShelfOverridePreview | null,
): TellOFacts => {
  const libraryShelves = withLoaderOverride(context.libraryShelves ?? [], override);
  const resolution = resolveDividerPolicy({
    policy,
    products: context.products,
    basePog: context.basePog,
    baseLibraryShelves: libraryShelves,
  });
  const dividers = new Set(
    [...resolution.needs].filter(([, need]) => need.needed).map(([code]) => code),
  );
  const keep = policy.keepFixtureDividers === true;
  const shelfResult = tellODividerShelves(context, dividers, keep, override);
  const loaderEvidence = getBaseLoaderEvidence(context.basePog, libraryShelves);
  const summary = summarizeBaseLoaders({ evidence: loaderEvidence, loaders: resolution.loaders });
  const loaderProducts = [...resolution.loaders.needs.values()].filter(
    (need) => need.needed,
  ).length;
  const depths =
    summary.depths.length > 0
      ? summary.depths
      : policy.loaderDepth && policy.loaderDepth > 0
        ? [policy.loaderDepth]
        : [];
  return {
    products: context.products.length,
    dividerProducts: dividers.size,
    shelves: shelfResult.shelves,
    dividerShelves: shelfResult.dividerShelves,
    hardware: resolution.hardware,
    loaderProducts,
    loaderShelves: summary.loaderShelves + summary.depthNotSet,
    loaderShelvesTotal: summary.shelves,
    loaderDepths: depths,
    loaderMode: resolution.loaders.mode,
    keep,
    typicalShelfWidth: getBaseDividerEvidence(context.basePog).typicalShelfWidth,
    typicalFacingWidth: typicalFacingWidth(context.basePog),
  };
};
